import React from "react";
import styled from "styled-components";
import { DialogActions, DialogContent, DialogTitle } from "@material-ui/core";

import { Button as BeautifulButton } from "js/Components/Button";
import WorkspaceController from "js/controllers/WorkspaceController";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import { FolderAccessList } from "js/react/components/FolderAccessList";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";
import { getFullUserDisplayName } from "js/react/views/UserOptions/panes/ManageUsersPane/Components/UsersList/UserListCommon";

const StyledBeautifulDialog = styled(BeautifulDialog)`
    &&& {
        .MuiDialog-paper {
            padding: 40px 50px;
            gap: 20px;
            margin: 0;
        }
    }
`;

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StyledDialogTitle = styled.div`
    &&& {
        color: #333;
        font-family: "Source Sans Pro";
        font-size: 23px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 20px;
        overflow: visible;
    }
`;

const Subtitle = styled.div`
    color: #666;
    /* popup/body */
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%; /* 20px */
    letter-spacing: -0.32px;
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        gap: 20px;
        padding: 0;
        margin: 0;
    }
`;

const ButtonStyles = `
    margin: 0;
    padding: 6px 16px;
`;

const CancelButton = styled(BeautifulButton)`
    &&& {
        ${ButtonStyles}
        color: #666;
    }
`;

const CreateButton = styled(BeautifulButton)`
    &&& {
        ${ButtonStyles}
    }
`;

export interface CreatePresentationInSharedFolderDialogProps {
    folderId: string;
    closeDialog: (confirmed?: boolean) => void;
}

export function CreatePresentationInSharedFolderDialog({
    folderId,
    closeDialog,
}: CreatePresentationInSharedFolderDialogProps) {
    const members = WorkspaceController.getUsersWithAccessToResourceFolder(folderId)
        .slice()
        .sort((a, b) =>
            getFullUserDisplayName(a.displayName, a.email)
                .localeCompare(getFullUserDisplayName(b.displayName, b.email))
        );

    return (
        <StyledBeautifulDialog closeDialog={() => closeDialog(false)} maxWidth="sm">
            <TitleSection>
                <StyledDialogTitle>
                    {t("Create in this shared folder?")}
                </StyledDialogTitle>
                <Subtitle>
                    {t("Everyone with access to this folder will be able to edit this presentation.")}
                </Subtitle>
            </TitleSection>
            <StyledDialogContent>
                <FolderAccessList members={members} />
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton
                    unframed
                    large
                    color={themeColors.mediumGray}
                    onClick={() => closeDialog(false)}
                >
                    {t("Cancel")}
                </CancelButton>
                <CreateButton
                    large
                    blue
                    onClick={() => closeDialog(true)}
                >
                    {t("Create")}
                </CreateButton>
            </StyledDialogActions>
        </StyledBeautifulDialog>
    );
}
